// services/motifAccumulationService.ts — règles de cumul par motif (Art. 71…)
import { supabase } from "../lib/supabaseClient";
import {
    mapAccumulationRule,
    mapMotifStatusRow,
    type AccumulationRule,
    type MotifCreated,
    type MotifStatusRow,
} from "../lib/disciplinary/motifAccumulation";
import { mapYellowSeriesRow, type YellowSeriesRow } from "../lib/disciplinary/yellowSeries";

export interface CreateAccumulationRuleInput {
    leagueIds: string[];
    seasonFrom: string;
    seasonTo: string | null;
    infractionIds: string[];
    threshold: number;
    suspensionMatches: number;
    repeats: boolean;
    articleCode: string;
    labelAr: string;
    labelFr: string;
    pvLabelAr: string | null;
    notes: string | null;
}

/** Every rule of the given leagues, newest first (the full timeline). */
export async function fetchAccumulationRules(leagueIds: string[]): Promise<AccumulationRule[]> {
    if (leagueIds.length === 0) return [];
    const { data, error } = await supabase
        .from("disciplinary_accumulation_rules")
        .select("*")
        .in("league_id", leagueIds)
        .order("season_from", { ascending: false })
        .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapAccumulationRule);
}

export async function createAccumulationRule(input: CreateAccumulationRuleInput): Promise<AccumulationRule[]> {
    const { data, error } = await supabase.rpc("create_accumulation_rule", {
        p_league_ids: input.leagueIds,
        p_season_from: input.seasonFrom,
        p_season_to: input.seasonTo,
        p_infraction_ids: input.infractionIds,
        p_threshold: input.threshold,
        p_suspension_matches: input.suspensionMatches,
        p_repeats: input.repeats,
        p_article_code: input.articleCode,
        p_label_ar: input.labelAr,
        p_label_fr: input.labelFr,
        p_pv_label_ar: input.pvLabelAr,
        p_notes: input.notes,
    });
    if (error) throw error;
    return ((data as any[]) ?? []).map(mapAccumulationRule);
}

/** Clôture (or reopening with null). The DB stamps closed_by / closed_at. */
export async function closeAccumulationRule(id: string, seasonTo: string | null): Promise<void> {
    const { error } = await supabase
        .from("disciplinary_accumulation_rules")
        .update({ season_to: seasonTo })
        .eq("id", id);
    if (error) throw error;
}

/** Refused by the DB (HINT accumulation_rule_triggered) once the rule has fired. */
export async function deleteAccumulationRule(id: string): Promise<void> {
    const { error } = await supabase.from("disciplinary_accumulation_rules").delete().eq("id", id);
    if (error) throw error;
}

export async function fetchYellowSeriesStatus(leagueId: string, season: string): Promise<YellowSeriesRow[]> {
    const { data, error } = await supabase.rpc("get_yellow_series_status", {
        p_league_id: leagueId,
        p_season: season,
    });
    if (error) throw error;
    return ((data as any[]) ?? []).map(mapYellowSeriesRow);
}

export async function fetchMotifAccumulationStatus(leagueId: string, season: string): Promise<MotifStatusRow[]> {
    const { data, error } = await supabase.rpc("get_motif_accumulation_status", {
        p_league_id: leagueId,
        p_season: season,
    });
    if (error) throw error;
    return ((data as any[]) ?? []).map(mapMotifStatusRow);
}

/**
 * Reconcile the motif rules for one player right after a yellow card is saved,
 * and return what was created so the entry can say so. The cache-invalidation
 * path reconciles too; the unique index absorbs the race.
 */
export async function reconcileMotifForPlayer(leagueId: string, season: string, playerId: string): Promise<MotifCreated[]> {
    const { data, error } = await supabase.functions.invoke("reconcile-yellow-commissions", {
        body: { leagueId, season, playerIds: [playerId], onlyMotif: true },
    });
    if (error) throw error;
    return ((data as any)?.motifCreated ?? []) as MotifCreated[];
}
